import { useEffect, useMemo, useRef, useState, useContext, Fragment } from 'react'
import type { AiMagicChatTurn } from '../../services/aiService'
import type { ProactiveSuggestionPayload } from '../../types/ai'
import type { ChartSpec } from '../../types/chart'
import { AttachmentPreviewDialog, type AttachmentPreviewItem } from '../Common/AttachmentPreviewDialog'
import { StatusChip, type StatusPillTone } from '../Common'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { Chart } from '../Common/Chart/Chart'
import { WorkspaceContext } from '../../context/workspaceContextCore'
import { saveDraftToLibrary } from '../../services/chartAiService'
import { QuickActionsTray } from './QuickActionsTray'
import { ProactiveSuggestionTray } from './ProactiveSuggestionTray'
import { hasBeenAnimated, markAnimated } from './lineTypewriterMemory'
import { parseCitationSegments, revealSegments, segmentTotalLength } from './citationSegments'
import { CiteChip } from './CiteChip'
import { AI_TIER_CATALOG, tierForModel } from '../../utils/aiTierCatalog'
import { useNls } from '../../nls/nlsProvider'
import { getLineStatusLabel } from '../AiMagicNls'

const LINE_STATUS_TONE: Record<NonNullable<AiMagicChatTurn['status']>, StatusPillTone> = {
  CONFIRMED: 'success',
  PENDING_APPROVAL: 'warning',
  PENDING_CLARIFICATION: 'info',
  REJECTED: 'danger',
}

export const LineChatBubble = ({
  turn,
  onApply,
  onReject,
  onAcceptSuggestion,
  onSnoozeSuggestion,
  hiddenSuggestionIds,
  actionError,
  onRetry,
  isLatestAssistantTurn = true,
  highlightClarification = false,
  onShowMeHow,
}: {
  turn: AiMagicChatTurn
  onApply?: () => void
  onReject?: () => void
  onAcceptSuggestion?: (suggestion: ProactiveSuggestionPayload) => void
  onSnoozeSuggestion?: (suggestionId: string) => void
  hiddenSuggestionIds?: Set<string>
  actionError?: string | null
  onRetry?: () => void
  isLatestAssistantTurn?: boolean
  highlightClarification?: boolean
  /** Called with the tourId when the user clicks the "Show me how" secondary button (U5). */
  onShowMeHow?: (tourId: string) => void
}) => {
  const { t } = useNls()
  const workspaceContext = useContext(WorkspaceContext)
  const canManageCharts = workspaceContext ? workspaceContext.canPerform('chart.manage') : false
  const activeWorkspaceId = workspaceContext?.activeWorkspaceId ?? ''

  const [previewItem, setPreviewItem] = useState<AttachmentPreviewItem | null>(null)
  const [savingChartIdx, setSavingChartIdx] = useState<number | null>(null)
  const [savedChartIndices, setSavedChartIndices] = useState<Set<number>>(() => new Set())
  const [chartSaveErrors, setChartSaveErrors] = useState<Record<number, string | null>>({})

  const handleSaveChartDraft = async (chartSpec: ChartSpec, idx: number) => {
    setSavingChartIdx(idx)
    setChartSaveErrors((prev) => ({ ...prev, [idx]: null }))
    try {
      await saveDraftToLibrary(activeWorkspaceId, chartSpec)
      setSavedChartIndices((prev) => new Set([...prev, idx]))
    } catch (err: unknown) {
      const errObj = err as { details?: { code?: string; limit?: number }; message?: string }
      if (errObj?.details?.code === 'charts.limit' || errObj?.message?.includes('limit')) {
        const limit = errObj?.details?.limit
        if (limit === 5) {
          setChartSaveErrors((prev) => ({ ...prev, [idx]: t('NLSChartsLimitReachedFree') }))
        } else if (limit === 50) {
          setChartSaveErrors((prev) => ({ ...prev, [idx]: t('NLSChartsLimitReachedPaid') }))
        } else {
          setChartSaveErrors((prev) => ({ ...prev, [idx]: t('NLSChartsLimitReachedGeneric') }))
        }
      } else {
        setChartSaveErrors((prev) => ({ ...prev, [idx]: errObj?.message || String(err) }))
      }
    } finally {
      setSavingChartIdx(null)
    }
  }

  const assistant = turn.role !== 'USER'
  const status = turn.status
  const actions = status === 'PENDING_APPROVAL' ? turn.actions ?? [] : []
  const suggestions = (turn.suggestions ?? []).filter((suggestion) => !hiddenSuggestionIds?.has(suggestion.id))
  const previews = turn.attachmentPreviews ?? []
  const alreadyAnimatedOnMount = useRef(hasBeenAnimated(turn.id)).current

  const segments = useMemo(
    () => parseCitationSegments(turn.text, turn.citations),
    [turn.text, turn.citations],
  )
  const totalLength = useMemo(() => segmentTotalLength(segments), [segments])

  const shouldAnimateText = useMemo(() => {
    if (!assistant || turn.role === 'WARNING' || !turn.text) return false
    if (!turn.id) return false
    if (!isLatestAssistantTurn) return false
    if (alreadyAnimatedOnMount) return false
    if (typeof window === 'undefined') return false
    if (typeof window.matchMedia !== 'function') return false
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }, [assistant, turn.role, turn.text, turn.id, isLatestAssistantTurn, alreadyAnimatedOnMount])

  const [revealCursor, setRevealCursor] = useState(() => shouldAnimateText ? 0 : totalLength)

  useEffect(() => {
    if (!shouldAnimateText) {
      setRevealCursor(totalLength)
      if (assistant && turn.id) markAnimated(turn.id)
      return
    }
    markAnimated(turn.id)
    let index = 0
    let cancelled = false
    setRevealCursor(0)
    const step = () => {
      if (cancelled) return
      const stepSize = Math.max(1, Math.ceil(totalLength / 90))
      index = Math.min(totalLength, index + stepSize)
      setRevealCursor(index)
      if (index < totalLength) {
        const nextChar = turn.text[Math.min(turn.text.length - 1, index - 1)]
        window.setTimeout(step, nextChar === '\n' ? 42 : 16)
      }
    }
    const timeout = window.setTimeout(step, 120)
    return () => {
      cancelled = true
      window.clearTimeout(timeout)
    }
  }, [shouldAnimateText, turn.id, totalLength, assistant, turn.text])

  const revealed = useMemo(() => revealSegments(segments, revealCursor), [segments, revealCursor])
  const isTyping = shouldAnimateText && revealCursor < totalLength
  const citationsById = useMemo(() => {
    const map = new Map<string, NonNullable<AiMagicChatTurn['citations']>[number]>()
    ;(turn.citations ?? []).forEach((cite) => map.set(cite.id, cite))
    return map
  }, [turn.citations])

  const isAssistantPlain = assistant && turn.role !== 'WARNING'
  const tierBadge = isAssistantPlain ? (() => {
    const badgeTier = tierForModel(turn.modelUsed, turn.tierAtSend)
    const descriptor = AI_TIER_CATALOG[badgeTier]
    const tooltip = t('NLSAiTierDraftedBy', { tierLongLabel: descriptor.longLabel })
    return (
      <span
        className={`lineTierBadge lineTierBadge--${descriptor.accentKey}`}
        title={tooltip}
        aria-label={tooltip}
      >
        <span className="lineTierBadgeDot" aria-hidden="true" />
        {descriptor.label}
      </span>
    )
  })() : null
  const statusChip = assistant && status ? (
    <StatusChip size="sm" tone={LINE_STATUS_TONE[status]}>{getLineStatusLabel(status, t)}</StatusChip>
  ) : null
  const textNodes = (
    <>
      {revealed.map((entry, idx) => {
        if (entry.segment.kind === 'text') {
          return <Fragment key={`t${idx}`}>{entry.visibleText}</Fragment>
        }
        const cite = citationsById.get(entry.segment.citationId)
        if (!cite) return null
        return (
          <CiteChip
            key={`c${idx}`}
            citation={cite}
            index={entry.segment.index}
            isRevealed={entry.isRevealed}
          />
        )
      })}
    </>
  )

  const containerClass = isAssistantPlain
    ? `lineFlatMessage${highlightClarification ? ' isOpenClarification' : ''}`
    : turn.role === 'WARNING'
      ? `lineWarningCard${highlightClarification ? ' isOpenClarification' : ''}`
      : `lineChatBubble isUser${highlightClarification ? ' isOpenClarification' : ''}`

  return (
    <>
      <article className={containerClass}>
        {isAssistantPlain ? (
          (tierBadge || statusChip || turn.creditSummary) ? (
            <div className="lineFlatMessage__meta">
              {statusChip}
              {tierBadge}
              {turn.creditSummary ? (
                <StatusChip size="sm" tone="neutral">{turn.creditSummary}</StatusChip>
              ) : null}
            </div>
          ) : null
        ) : turn.role === 'WARNING' ? (
          <header className="lineWarningCard__header">
            <span className="lineWarningCard__label">{t('NLSAiLabelSystem')}</span>
          </header>
        ) : (
          <header className="lineChatBubbleHeader">
            <span className="lineChatName">{t('NLSAiLabelYou')}</span>
          </header>
        )}
        <span
          className={isAssistantPlain
            ? `lineFlatMessage__text${isTyping ? ' isTyping' : ''}`
            : turn.role === 'WARNING'
              ? `lineWarningCard__text${isTyping ? ' isTyping' : ''}`
              : `lineChatText${isTyping ? ' isTyping' : ''}`}
        >
          {textNodes}
        </span>
        {previews.length > 0 && (
          <div className="lineAttachmentPreviewStrip" aria-label={t('NLSAiAriaAttachedPreviews')}>
            {previews.map((item) => (
              <Pressable
                key={item.id}
                type="button"
                className={`lineAttachmentPreview lineAttachmentPreview--${item.kind}`}
                onClick={() => setPreviewItem(item)}
              >
                {item.kind === 'image' && item.url && !item.isUnavailable ? (
                  <img src={item.url} alt="" />
                ) : (
                  <span aria-hidden="true">
                    {item.kind === 'timeline' ? 'T' : item.kind === 'audio' ? 'A' : item.kind === 'video' ? 'V' : item.kind === 'link' ? 'L' : item.kind === 'chat' ? 'C' : item.kind === 'form' || item.kind === 'form_responses' ? 'F' : item.kind === 'design' ? 'D' : item.kind === 'spreadsheet' || item.kind === 'gsheet' ? 'S' : 'F'}
                  </span>
                )}
                <strong>{item.label}</strong>
                {(item.isUnavailable || item.sizeLabel || item.summary) && <small>{item.isUnavailable ? (item.unavailableReason || t('NLSAiLabelAttachmentUnavailable')) : (item.sizeLabel ?? item.summary)}</small>}
              </Pressable>
            ))}
          </div>
        )}
        {turn.charts && turn.charts.length > 0 && (
          <div className="lineChatChartsGrid" data-testid="line-chat-charts">
            {turn.charts.map((chartSpec, idx) => (
              <div key={idx} className="lineChatChartCard" data-testid={`line-chat-chart-${idx}`}>
                <header className="lineChatChartHeader">
                  <strong>{chartSpec.title || t('NLSChartsPreviewCardTitle')}</strong>
                  <span className="lineChatChartBadge">{chartSpec.type}</span>
                </header>
                {chartSpec.subtitle && <p className="lineChatChartSub">{chartSpec.subtitle}</p>}
                <div className="lineChatChartPreview">
                  <Chart
                    spec={chartSpec}
                    data={{
                      series: [{ key: 'preview', label: chartSpec.title || 'Data', points: [{ x: 'A', y: 10 }, { x: 'B', y: 25 }, { x: 'C', y: 18 }] }],
                      asOf: new Date().toISOString(),
                    }}
                    height={140}
                  />
                </div>
                {chartSaveErrors[idx] && (
                  <p className="lineChatChartError" role="alert" data-testid={`chart-save-error-${idx}`}>
                    {chartSaveErrors[idx]}
                  </p>
                )}
                {canManageCharts && (
                  <footer className="lineChatChartFooter">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void handleSaveChartDraft(chartSpec, idx)}
                      loading={savingChartIdx === idx}
                      disabled={savedChartIndices.has(idx) || savingChartIdx === idx}
                      data-testid={`save-chart-draft-btn-${idx}`}
                    >
                      {savedChartIndices.has(idx) ? t('NLSChartsSaveSuccess') : t('NLSChartsSaveToLibrary')}
                    </Button>
                  </footer>
                )}
              </div>
            ))}
          </div>
        )}
        {turn.automationExecution?.actions?.length ? (
          <section className="lineExecutionResults" aria-label={t('NLSAiExecutionResultsAria')}>
            <div className="lineExecutionResultsHeader">
              <strong>{t('NLSAiExecutionDetails')}</strong>
              <span>
                {t('NLSAiExecutionCompletedFraction', {
                  completed: turn.automationExecution.actions.filter((action) => action.outcome === 'SUCCESS').length,
                  total: turn.automationExecution.actions.length,
                })}
              </span>
            </div>
            <div className="lineExecutionResultsList">
              {turn.automationExecution.actions.map((action) => (
                <article
                  key={action.actionId}
                  className={`lineExecutionResultCard is-${action.outcome}`}
                >
                  <header>
                    <strong>{action.heading}</strong>
                    <span>{action.outcome === 'SUCCESS' ? t('NLSAiExecutionDone') : t('NLSAiExecutionFailed')}</span>
                  </header>
                  <p>{action.summary}</p>
                  {action.errorMessage ? <small>{action.errorMessage}</small> : null}
                </article>
              ))}
            </div>
            {turn.automationExecution.warnings.length ? (
              <div className="lineExecutionWarnings">
                {turn.automationExecution.warnings.map((warning) => <small key={warning}>{warning}</small>)}
              </div>
            ) : null}
          </section>
        ) : null}
        {suggestions.length > 0 && onAcceptSuggestion && onSnoozeSuggestion && (
          <ProactiveSuggestionTray
            suggestions={suggestions}
            onAccept={onAcceptSuggestion}
            onSnooze={onSnoozeSuggestion}
          />
        )}
        {actions.length > 0 && onApply && onReject && (
          <QuickActionsTray
            actions={actions}
            onApply={onApply}
            onReject={onReject}
            error={actionError}
            onRetry={onRetry}
          />
        )}
        {isLatestAssistantTurn && turn.lineExecuteRegistryTourId && onShowMeHow && (
          <div className="lineExecuteShowMeHow">
            <Button
              id={`line-show-me-how-${turn.id ?? 'turn'}`}
              size="sm"
              variant="secondary"
              className="lineExecuteShowMeHowBtn"
              onClick={() => onShowMeHow(turn.lineExecuteRegistryTourId!)}
            >
              Show me how
            </Button>
          </div>
        )}
      </article>
      <AttachmentPreviewDialog open={Boolean(previewItem)} item={previewItem} onClose={() => setPreviewItem(null)} />
    </>
  )
}
